import { roundRupees } from './money';

export const emptyWholesaleLine = () => ({
  category: '',
  model: '',
  modelName: '',
  modelNumber: '',
  unitPrice: 0,
  currentStock: 0,
  qty: 1,
  totalValue: 0,
  advance: 0,
  colorRequest: '',
  showColorNote: false,
});

export const getWholesaleQtyError = (item) => {
  if (!item.model) return null;
  if (item.qty <= 0) return 'Quantity must be greater than 0';
  if (item.qty > item.currentStock) return `Insufficient stock. Available: ${item.currentStock}`;
  return null;
};

export const buildWholesaleLineFromInventory = (inv, qty = 1) => {
  const totalValue = roundRupees(inv.price * qty);
  return {
    category: inv.size,
    model: inv._id,
    modelName: inv.modelName,
    modelNumber: inv.modelNumber,
    unitPrice: roundRupees(inv.price),
    currentStock: inv.currentStock,
    qty,
    totalValue,
    advance: roundRupees(Math.ceil(totalValue * 0.4)),
    colorRequest: '',
    showColorNote: false,
  };
};

export const recalcWholesaleLine = (line, qty) => {
  const quantity = parseInt(qty, 10) || 0;
  const totalValue = roundRupees(line.unitPrice * quantity);
  return {
    ...line,
    qty: quantity,
    totalValue,
    advance: roundRupees(Math.ceil(totalValue * 0.4)),
  };
};

export const mapWholesaleLinesToOrderItems = (lines) =>
  lines.map((item) => ({
    model: item.model,
    qty: item.qty,
    advance: roundRupees(item.advance),
    colorRequest: item.colorRequest?.trim() || undefined,
  }));

/** Distribute an order-level advance across lines proportional to catalog line totals. */
export const distributeAdvanceAcrossLines = (lines, totalAdvance) => {
  if (!lines.length) return [];
  const target = roundRupees(Math.max(0, totalAdvance));
  const catalogTotal = roundRupees(lines.reduce((sum, item) => sum + item.totalValue, 0));

  if (catalogTotal <= 0) {
    const even = Math.floor(target / lines.length);
    let allocated = 0;
    return lines.map((line, index) => {
      const advance =
        index === lines.length - 1
          ? roundRupees(target - allocated)
          : even;
      allocated = roundRupees(allocated + advance);
      return { ...line, advance };
    });
  }

  let allocated = 0;
  return lines.map((line, index) => {
    let advance;
    if (index === lines.length - 1) {
      advance = roundRupees(target - allocated);
    } else {
      advance = roundRupees(Math.floor((line.totalValue / catalogTotal) * target));
      allocated = roundRupees(allocated + advance);
    }
    return { ...line, advance };
  });
};

export const calcWholesaleTotals = (lines, options = {}) => {
  const { settledPrice, orderAdvance } = options;
  const catalogGrandTotal = roundRupees(lines.reduce((sum, item) => sum + item.totalValue, 0));
  const hasSettled =
    settledPrice !== '' &&
    settledPrice !== null &&
    settledPrice !== undefined &&
    Number.isFinite(Number(settledPrice));
  const grandTotal = hasSettled
    ? roundRupees(Number(settledPrice))
    : catalogGrandTotal;

  const lineAdvanceSum = roundRupees(lines.reduce((sum, item) => sum + item.advance, 0));
  const totalAdvance =
    hasSettled && orderAdvance !== undefined && orderAdvance !== null
      ? roundRupees(orderAdvance)
      : lineAdvanceSum;

  const balance = roundRupees(grandTotal - totalAdvance);
  const minAdvanceRequired = roundRupees(Math.ceil(grandTotal * 0.4));
  const isAdvanceSufficient = totalAdvance >= minAdvanceRequired;
  return {
    catalogGrandTotal,
    grandTotal,
    totalAdvance,
    balance,
    minAdvanceRequired,
    isAdvanceSufficient,
    hasSettled,
  };
};

export const validateWholesaleCustomer = (customerName, phone) => {
  if (!customerName.trim()) {
    return 'Customer name is required';
  }
  if (!/^[6-9]\d{9}$/.test(phone.trim())) {
    return 'Please enter a valid 10-digit Indian mobile number';
  }
  return null;
};

export const WHOLESALE_CATEGORY_ORDER = ['small', 'medium', 'large', 'pen'];

export const WHOLESALE_CATEGORY_LABELS = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  pen: 'Pen/Artistic',
};
